import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import typography from '../../styles/typography';
import tokens from '../../constants/styles';
import MermaidDiagram from './MermaidDiagram';

const MONO = "Consolas, 'Courier New', 'DejaVu Sans Mono', monospace";

/**
 * react-markdown v10 drops the `inline` prop, so a fence is identified by its language
 * class. Coerced rather than type-checked because hast hands className over as either a
 * string or an array of classes depending on the source.
 */
const isMermaidClass = (className) => String(className ?? '').includes('language-mermaid');

/** True when this <pre> wraps a mermaid fence - the diagram renders without code-block chrome. */
function wrapsMermaid(children) {
  const child = React.Children.toArray(children)[0];
  return React.isValidElement(child) && isMermaidClass(child.props.className);
}

/**
 * Distinguish a fenced block from an inline span without the `inline` prop v9 removed.
 *
 * Two signals, because neither is sufficient alone: a fence usually carries a
 * `language-*` class, but one written without a language carries no class at all and
 * would otherwise read as inline. Such a fence still spans several lines, since its
 * ``` markers sit on their own, whereas an inline span sits within one line.
 *
 * Known gap: a *single-line indented* code block (four spaces, one line) has neither
 * signal and is treated as inline. Fenced blocks are unaffected at any length.
 */
function isBlockCode(node, className) {
  if (String(className ?? '').includes('language-')) return true;
  const position = node && node.position;
  return !!position && position.start.line !== position.end.line;
}

function MarkdownViewer({ fileUrl, authenticatedFetch }) {
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchMarkdown() {
      try {
        const res = await authenticatedFetch(fileUrl);
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`);
        const buffer = await res.arrayBuffer();
        const text = new TextDecoder('utf-8').decode(buffer);
        setContent(text);
      } catch (err) {
        setError(err.message || 'Error loading markdown');
      }
    }
    if (fileUrl) fetchMarkdown();
  }, [fileUrl]);

  if (error) return <div style={styles.container}><p style={{color:'#ff6b6b'}}>Error: {error}</p></div>;

  return (
    <div style={styles.container}>
      <style dangerouslySetInnerHTML={{__html: `
        .md-viewer pre, .md-viewer pre *, .md-viewer code {
          font-family: ${MONO} !important;
          font-size: 12px !important;
          line-height: 1.5 !important;
        }
        .md-viewer table { border-collapse: collapse; width: 100%; font-size: 13px; }
        .md-viewer th { border: 1px solid var(--mp-border); padding: 7px 12px; background: var(--mp-dark-alt); text-align: left; font-weight: 600; color: var(--mp-text); }
        .md-viewer td { border: 1px solid var(--mp-border); padding: 6px 12px; color: var(--mp-text); }
        .md-viewer tr:nth-child(even) td { background: var(--mp-hover); }
        .md-viewer a { color: var(--mp-iteration); text-decoration: none; }
        .md-viewer hr { border: none; border-top: 1px solid var(--mp-border); margin: 1.5rem 0; }
        .md-viewer blockquote { border-left: 3px solid var(--mp-iteration); margin: 1rem 0; padding: 0.5rem 1rem; background: var(--mp-dark-alt); border-radius: 0 4px 4px 0; color: var(--mp-text-muted); }
        .md-viewer ul, .md-viewer ol { padding-left: 1.5rem; margin: 0.5rem 0 0.8rem; }
        .md-viewer li { margin: 0.2rem 0; font-size: 13px; color: var(--mp-text); line-height: 1.6; }
        .md-viewer p { margin: 0 0 0.8rem; font-size: 13px; color: var(--mp-text); line-height: 1.6; }
        .md-viewer strong { color: var(--mp-text); }
      `}} />
      <div className="md-viewer">
        {content ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 style={styles.h1} {...props} />,
              h2: ({node, ...props}) => <h2 style={styles.h2} {...props} />,
              h3: ({node, ...props}) => <h3 style={styles.h3} {...props} />,
              h4: ({node, ...props}) => <h4 style={styles.h4} {...props} />,
              pre: ({node, ...props}) => wrapsMermaid(props.children)
                ? props.children
                : <pre style={styles.pre} {...props} />,
              code: ({node, ...props}) => isMermaidClass(props.className)
                ? <MermaidDiagram code={String(props.children)} />
                : isBlockCode(node, props.className)
                  ? <code {...props} />
                  : <code style={styles.inlineCode} {...props} />,
              table: ({node, ...props}) => <div style={{overflowX:'auto',marginBottom:'1.2rem'}}><table {...props} /></div>,
            }}
          >{content}</ReactMarkdown>
        ) : (
          <p style={{color: tokens.colors.text.muted}}>Loading...</p>
        )}
      </div>
    </div>
  );
}

// Themed via the shared tokens so rendered markdown is readable in both palettes.
const styles = {
  container: {
    minHeight: '600px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colors.border}`,
    overflow: 'auto',
    padding: '1.5rem 2rem',
    backgroundColor: tokens.colors.dark,
    fontFamily: typography.primary,
    fontSize: '13px',
    color: tokens.colors.text.light,
  },
  h1: { fontSize: '1.6rem', fontWeight: 600, color: tokens.colors.text.light, margin: '1.5rem 0 0.5rem', lineHeight: 1.3, borderBottom: `1px solid ${tokens.colors.border}`, paddingBottom: '0.4rem' },
  h2: { fontSize: '1.25rem', fontWeight: 600, color: tokens.colors.text.light, margin: '1.4rem 0 0.4rem', lineHeight: 1.3, borderBottom: `1px solid ${tokens.colors.border}`, paddingBottom: '0.3rem' },
  h3: { fontSize: '1.05rem', fontWeight: 600, color: tokens.colors.text.light, margin: '1.2rem 0 0.3rem', lineHeight: 1.4 },
  h4: { fontSize: '0.95rem', fontWeight: 600, color: tokens.colors.text.muted, margin: '1rem 0 0.3rem' },
  pre: { background: tokens.colors.darkAlt, padding: '1rem', borderRadius: '6px', overflow: 'auto', margin: '0.8rem 0', fontFamily: MONO, border: `1px solid ${tokens.colors.border}` },
  inlineCode: { fontFamily: MONO, fontSize: '12px', background: tokens.colors.darkAlt, padding: '1px 5px', borderRadius: '3px', color: tokens.colors.text.light, border: `1px solid ${tokens.colors.border}` },
};

export default MarkdownViewer;
